import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { IoIosArrowBack } from 'react-icons/io'
import { MdSend } from 'react-icons/md'
import { BiPlus } from 'react-icons/bi'
import { getSocket } from '../store/socketClient'
import * as authAction from '../store/authAction'
import { useAuthGate } from '../comps/authPrompt'
import UserAvatar from '../comps/userAvatar'
import { createRoomRtc } from '../utils/roomRtc'
import '../styles/rooms.css'

const Room = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(state => state.authReduse)
  const { requireAuth, promptEl } = useAuthGate()
  const [room, setRoom] = useState(null)
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const [speaking, setSpeaking] = useState(false)
  const [needMic, setNeedMic] = useState(false)
  const [needTap, setNeedTap] = useState(false)
  const [waiting, setWaiting] = useState(null)
  const chatRef = useRef(null)
  const rtcRef = useRef(null)
  const speakingRef = useRef(false)
  const userRef = useRef(user)
  userRef.current = user

  useEffect(() => {
    const userData = localStorage.getItem('userdata')
    if (!userData) return undefined
    try {
      const { id: authId, pid } = JSON.parse(userData)
      dispatch(authAction.auth(authId, pid))
    } catch (err) {}
    return undefined
  }, [dispatch])

  useEffect(() => {
    if (!user.id) return undefined
    const socket = getSocket()
    socket.emit('con', String(user.id))
    const rtc = createRoomRtc({
      getSocket,
      roomId: id,
      myId: user.id,
      onError: (msg) => setError(msg),
      onNeedTap: (blocked) => setNeedTap(Boolean(blocked))
    })
    rtcRef.current = rtc
    let joined = false

    const joinLive = () => {
      const me = userRef.current
      if (!me || !me.id) return
      socket.emit('con', String(me.id))
      socket.emit('room:join', {
        roomId: id,
        user: {
          id: me.id,
          name: me.name || me.email,
          photo: me.photo
        }
      })
    }

    const askMic = () => rtc.startMic()
      .then(() => {
        rtc.setMuted(true)
        setNeedMic(false)
      })
      .catch(() => {
        setNeedMic(true)
        setError('Allow the microphone to speak in this room')
      })

    const onState = async (next) => {
      setWaiting(null)
      setRoom(next)
      setMessages(next.messages || [])
      const me = (next.members || []).find(member => String(member.id) === String(user.id))
      const nextSpeak = Boolean(me && me.speaking)
      speakingRef.current = nextSpeak
      setSpeaking(nextSpeak)
      if (!joined) {
        joined = true
        await askMic()
      }
      rtc.setMuted(!nextSpeak)
      await rtc.sync(next.members || []).catch(() => {})
    }
    const onWaiting = (info) => setWaiting(info)
    const onAdmitted = (payload) => {
      setWaiting(null)
      if (payload && payload.room) onState(payload.room)
    }
    const onDenied = (payload) => {
      navigate('/rooms', { replace: true, state: { roomFull: (payload && payload.msg) || 'The host did not let you in.' } })
    }
    const onMessage = (message) => {
      setMessages(prev => [...prev, message].slice(-80))
    }
    const onSignal = (payload) => rtc.handleSignal(payload)
    const onKicked = () => {
      setError('The host removed you from the room')
      navigate('/rooms', { replace: true })
    }
    const onMuted = () => {
      speakingRef.current = false
      setSpeaking(false)
      rtc.setMuted(true)
    }
    const onErr = (payload) => {
      const msg = (payload && payload.msg) || 'Room error'
      if (/full/i.test(msg)) {
        navigate('/rooms', { replace: true, state: { roomFull: msg } })
        return
      }
      setError(msg)
    }

    socket.on('room:state', onState)
    socket.on('room:waiting', onWaiting)
    socket.on('room:admitted', onAdmitted)
    socket.on('room:denied', onDenied)
    socket.on('room:message', onMessage)
    socket.on('room:signal', onSignal)
    socket.on('room:kicked', onKicked)
    socket.on('room:muted', onMuted)
    socket.on('room:error', onErr)
    socket.on('connect', joinLive)
    if (socket.connected) joinLive()

    return () => {
      socket.emit('room:leave', { roomId: id })
      socket.off('room:state', onState)
      socket.off('room:waiting', onWaiting)
      socket.off('room:admitted', onAdmitted)
      socket.off('room:denied', onDenied)
      socket.off('room:message', onMessage)
      socket.off('room:signal', onSignal)
      socket.off('room:kicked', onKicked)
      socket.off('room:muted', onMuted)
      socket.off('room:error', onErr)
      socket.off('connect', joinLive)
      rtc.destroy()
      rtcRef.current = null
    }
  }, [id, navigate, user.id])

  useEffect(() => {
    const node = chatRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [messages])

  const leave = () => {
    const socket = getSocket()
    socket.emit('room:leave', { roomId: id })
    navigate('/rooms')
  }

  const toggleSpeak = async (member) => {
    if (!requireAuth()) return
    const mine = String(member.id) === String(user.id)
    const socket = getSocket()
    if (mine) {
      try {
        await rtcRef.current.startMic()
        rtcRef.current.unlockPlayback()
        const nextSpeak = !speaking
        rtcRef.current.setMuted(!nextSpeak)
        socket.emit('room:speak', { roomId: id, speaking: nextSpeak })
        setNeedMic(false)
      } catch (err) {
        setNeedMic(true)
        setError('Microphone permission is needed to speak')
      }
      return
    }
    if (String(room && room.hostId) === String(user.id) && member.speaking) {
      socket.emit('room:mute', { roomId: id, userid: member.id })
    }
  }

  const kick = (event, memberId) => {
    event.stopPropagation()
    getSocket().emit('room:kick', { roomId: id, userid: memberId })
  }

  const sendChat = (event) => {
    event.preventDefault()
    if (!requireAuth()) return
    const body = text.trim()
    if (!body) return
    getSocket().emit('room:chat', { roomId: id, text: body })
    setText('')
  }

  const members = (room && room.members) || []
  const isHost = room && String(room.hostId) === String(user.id)
  const bg = room ? room.background : 'night'
  const openSeats = Math.max(0, Number(room && room.limit ? room.limit : 0) - members.length)
  const emptyShown = Math.min(openSeats, 12)
  const emptyExtra = openSeats - emptyShown
  const pending = (room && room.pending) || []

  if (!user.id) {
    return (
      <section className="RoomPage">
        <div className="roomBackdrop roomsBg--night" aria-hidden="true" />
        {promptEl}
        <button type="button" className="roomLeave" onClick={() => navigate('/auth')}>
          Sign in to join
        </button>
      </section>
    )
  }

  if (waiting) {
    return (
      <section className="RoomPage">
        <div className={`roomBackdrop roomsBg--${waiting.background || 'night'}`} aria-hidden="true" />
        {promptEl}
        <header className="roomTop">
          <button type="button" className="roomBack" onClick={leave} aria-label="Leave">
            <IoIosArrowBack />
          </button>
          <div className="roomTopText">
            <h1>{waiting.name}</h1>
            <p>Waiting for @{waiting.hostName} to let you in</p>
          </div>
          <button type="button" className="roomLeave" onClick={leave}>Cancel</button>
        </header>
        <div className="roomWaiting">
          <p>This room needs permission to enter. Hang on while the host reviews your request.</p>
        </div>
      </section>
    )
  }

  return (
    <section className="RoomPage" onPointerDown={() => rtcRef.current && rtcRef.current.unlockPlayback()}>
      <div className={`roomBackdrop roomsBg--${bg}`} aria-hidden="true" />
      {promptEl}
      <Helmet>
        <title>{room ? room.name : 'Room'}</title>
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <header className="roomTop">
        <button type="button" className="roomBack" onClick={leave} aria-label="Leave room">
          <IoIosArrowBack />
        </button>
        <div className="roomTopText">
          <h1>{room ? room.name : 'Room'}</h1>
          <p>{members.length}/{room ? room.limit : 0} in the room</p>
        </div>
        <button type="button" className="roomLeave" onClick={leave}>Leave</button>
      </header>
      {isHost && pending.length > 0 && (
        <div className="roomPending">
          {pending.map(person => (
            <div key={person.id} className="roomPendingRow">
              <UserAvatar className="roomPendingFace" src={person.photo} name={person.name} alt="" />
              <p>{person.name} wants to enter</p>
              <button type="button" onClick={() => getSocket().emit('room:admit', { roomId: id, userid: person.id })}>Let in</button>
              <button type="button" className="isNo" onClick={() => getSocket().emit('room:deny', { roomId: id, userid: person.id })}>No</button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="roomsError roomsError--live">{error}</p>}
      {needMic && (
        <div className="roomMicBar">
          <p>This room needs your microphone so people can hear you speak.</p>
          <button
            type="button"
            onClick={async () => {
              try {
                await rtcRef.current.startMic()
                rtcRef.current.unlockPlayback()
                rtcRef.current.setMuted(true)
                setNeedMic(false)
                setNeedTap(false)
                setError('')
              } catch (err) {
                setError('Microphone permission is needed to speak')
              }
            }}
          >
            Allow mic
          </button>
        </div>
      )}
      {needTap && !needMic && (
        <div className="roomMicBar">
          <p>Tap to hear people in this room.</p>
          <button
            type="button"
            onClick={() => {
              if (rtcRef.current) rtcRef.current.unlockPlayback()
              setNeedTap(false)
            }}
          >
            Enable sound
          </button>
        </div>
      )}

      <div className="roomPeople">
        {members.map(member => {
          const mine = String(member.id) === String(user.id)
          return (
            <button
              type="button"
              key={member.id}
              className={`roomPerson${member.speaking ? ' isSpeaking' : ''}${mine ? ' isMine' : ''}`}
              onClick={() => toggleSpeak(member)}
            >
              {isHost && !mine && (
                <span className="roomKick" onClick={event => kick(event, member.id)} title="Kick">×</span>
              )}
              <UserAvatar className="roomPersonFace" src={member.photo} name={member.name} alt="" />
              <span className="roomPersonName">
                {member.name}
                {member.host ? ' · host' : ''}
                {mine ? ' · you' : ''}
              </span>
              <span className="roomPersonHint">{mine ? (speaking ? 'Tap to stop' : 'Tap to speak') : member.speaking ? (isHost ? 'Tap to mute' : 'Speaking') : 'Listening'}</span>
            </button>
          )
        })}
        {Array.from({ length: emptyShown }).map((_, index) => (
          <div key={`open-${index}`} className="roomPerson roomPerson--empty">
            <span className="roomPersonFace roomPersonFace--empty" aria-hidden="true">
              <BiPlus />
            </span>
            <span className="roomPersonName">Open</span>
            <span className="roomPersonHint">Waiting</span>
          </div>
        ))}
        {emptyExtra > 0 && (
          <div className="roomPerson roomPerson--empty">
            <span className="roomPersonFace roomPersonFace--empty">+{emptyExtra}</span>
            <span className="roomPersonName">More</span>
            <span className="roomPersonHint">Open seats</span>
          </div>
        )}
      </div>

      <div className="roomChat" ref={chatRef}>
        {messages.map(message => (
          <p key={message.id} className={message.type === 'system' ? 'roomSys' : 'roomMsg'}>
            {message.type === 'system' ? message.text : <><strong>{message.name}</strong> {message.text}</>}
          </p>
        ))}
      </div>

      <form className="roomComposer" onSubmit={sendChat}>
        <input
          value={text}
          onChange={event => setText(event.target.value)}
          placeholder="Send a message"
          maxLength={400}
        />
        <button type="submit" aria-label="Send"><MdSend /></button>
      </form>
    </section>
  )
}

export default Room
